import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import styles from "./prTable.module.scss";

interface PREntry {
  prId: string;
  specialist: {
    name: string;
    initials: string;
    avatarColor?: string;
  };
  status: "In Review" | "Approved" | "Delayed" | "Pending";
  assignedDate: string;
  estDate: string;
  delay: {
    type: "on-track" | "delayed";
    days?: number;
  };
}

interface PRTableProps {
  title?: string;
  data?: PREntry[];
}

const PRTable: React.FC<PRTableProps> = ({
  title = "Active Purchase Requisitions",
  data = [],
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "In Review":
        return "#0066cc";
      case "Approved":
        return "#00d2aa";
      case "Delayed":
        return "#ff6b6b";
      case "Pending":
        return "#ffa94d";
      default:
        return "#666666";
    }
  };

  const getAvatarColor = (index: number): string => {
    const colors = [
      "#00d2aa",
      "#5b6b9e",
      "#00d2aa",
      "#5b6b9e",
      "#00d2aa",
      "#00d2aa",
    ];
    return colors[index % colors.length];
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.tableWrapper}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
      </Box>

      <TableContainer className={styles.tableWrapper}>
        <Table className={styles.table}>
          <TableHead>
            <TableRow className={styles.headerRow}>
              <TableCell className={styles.headerCell}>PR ID</TableCell>
              <TableCell className={styles.headerCell}>
                SOURCING SPECIALIST
              </TableCell>
              <TableCell className={styles.headerCell}>STATUS</TableCell>
              <TableCell className={styles.headerCell}>ASSIGNED DATE</TableCell>
              <TableCell className={styles.headerCell}>EST. DATE</TableCell>
              <TableCell className={styles.headerCell}>DELAY</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index} className={styles.bodyRow}>
                <TableCell className={styles.bodyCell}>
                  <Typography className={styles.prId}>{row.prId}</Typography>
                </TableCell>
                <TableCell className={styles.bodyCell}>
                  <Box className={styles.specialistCell}>
                    <Avatar
                      className={styles.avatar}
                      sx={{
                        backgroundColor:
                          row.specialist.avatarColor || getAvatarColor(index),
                      }}
                    >
                      {row.specialist.initials}
                    </Avatar>
                    <Typography className={styles.specialistName}>
                      {row.specialist.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell className={styles.bodyCell}>
                  <Chip
                    label={row.status}
                    className={styles.statusChip}
                    sx={{
                      backgroundColor: `${getStatusColor(row.status)}15`,
                      color: getStatusColor(row.status),
                    }}
                  />
                </TableCell>
                <TableCell className={styles.bodyCell}>
                  <Typography className={styles.date}>
                    {row.assignedDate}
                  </Typography>
                </TableCell>
                <TableCell className={styles.bodyCell}>
                  <Typography className={styles.date}>{row.estDate}</Typography>
                </TableCell>
                <TableCell className={styles.bodyCell}>
                  {row.delay.type === "on-track" ? (
                    <Box className={styles.delayOnTrack}>
                      <CheckCircleIcon className={styles.onTrackIcon} />
                      <Typography className={styles.onTrackText}>
                        On track
                      </Typography>
                    </Box>
                  ) : (
                    <Box className={styles.delayDelayed}>
                      <WarningIcon className={styles.delayedIcon} />
                      <Typography className={styles.delayedText}>
                        {row.delay.days} days
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PRTable;
